import {FC, useEffect, useRef, useState} from 'react'
import {Accordion, Badge, Button, Card, Form} from 'react-bootstrap'
import Select, {SelectInstance} from 'react-select'
import {updateUserByUnitsXPerfil} from '../../../../../services/users'
import {showMessage} from '../../../../../shared/Alerts'

interface UserItemProps {
  onUserUpdate?: (user: any) => void
  onCheck?: (checked: boolean) => void
  checked?: boolean
  id: string
  dni: string
  nombre: string
  companyId: string
  roles: {id: string; name: string}[]
  unidades: {codUnid: string; DesUnid: string}[]
  rolesUnidad: {
    company: string
    rol: {
      id: string
      unidades: {id: string; name: string}[]
    }[]
  } | null
  rolesUnidadFull: {
    company: string
    rol: {
      id: string
      unidades: {id: string; name: string}[]
    }[]
  }[]
}

interface ISelectOption {
  value: string
  label: string
}

interface UserUpdated {
  dni: string
  roleUnidad: {
    company: string
    rol: {
      id: string
      unidades: {id: string; name: string}[]
    }[]
  }[]
}

const defaultOptions = {label: 'Selecione un rol', value: ''}

const UserItem: FC<UserItemProps> = ({
  id,
  dni,
  nombre,
  roles,
  unidades,
  companyId,
  rolesUnidad,
  rolesUnidadFull,
  onCheck,
  onUserUpdate,
  checked,
}) => {
  const [selectedRole, setSelectedRole] = useState('')
  const [loading, setLoading] = useState(false)
  const rolesOptions = [defaultOptions, ...roles.map((rol) => ({value: rol.id, label: rol.name}))]
  const selectRef = useRef<SelectInstance<ISelectOption | null>>(null)

  const existValue = rolesUnidadFull.some((ru) => ru.company === companyId)
  const rolInitValue = rolesUnidad
    ? rolesUnidad.rol
    : roles.map((rol) => ({
        id: rol.id,
        unidades: [],
      }))

  const initValue = existValue
    ? rolesUnidadFull
    : [
        {
          company: companyId,
          rol: rolInitValue,
        },
        ...rolesUnidadFull,
      ]

  const [userUpdated, setUserUpdated] = useState<UserUpdated>({
    dni,
    roleUnidad: initValue,
  })

  const handleCheck: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const isChecked = e.target.checked
    const value = e.target.id

    if (isChecked && value !== 'all') {
      const unidadItem = unidades.find((u) => u.codUnid === value)
      const unidadesByRol =
        userUpdated.roleUnidad
          ?.find((ru) => ru.company === companyId)
          ?.rol.find((rol) => rol.id === selectedRole)?.unidades ?? []
      const unidadesFinal = [...unidadesByRol, {id: unidadItem!.codUnid, name: unidadItem!.DesUnid}]

      setUserUpdated((prev) => ({
        ...prev,
        roleUnidad: prev.roleUnidad.map((ru) => {
          if (ru.company === companyId) {
            return {
              ...ru,
              rol: ru.rol.map((rol) => {
                if (rol.id === selectedRole) {
                  return {
                    id: rol.id,
                    unidades: unidadesFinal,
                  }
                }

                return rol
              }),
            }
          }

          return ru
        }),
      }))
    }

    if (!isChecked && value !== 'all') {
      const unidadesByRol =
        userUpdated.roleUnidad
          ?.find((ru) => ru.company === companyId)
          ?.rol.find((rol) => rol.id === selectedRole)?.unidades ?? []
      const unidadesByRolFilter = unidadesByRol.filter((u) => u.id !== value)

      setUserUpdated((prev) => ({
        ...prev,
        roleUnidad: prev.roleUnidad.map((ru) => {
          if (ru.company === companyId) {
            return {
              ...ru,
              rol: ru.rol.map((rol) => {
                if (rol.id === selectedRole) {
                  return {
                    id: rol.id,
                    unidades: unidadesByRolFilter,
                  }
                }

                return rol
              }),
            }
          }

          return ru
        }),
      }))
    }

    if (value === 'all') {
      const unidadesMap = isChecked ? unidades.map((u) => ({id: u.codUnid, name: u.DesUnid})) : []

      setUserUpdated((prev) => ({
        ...prev,
        roleUnidad: prev.roleUnidad.map((ru) => {
          if (ru.company === companyId) {
            return {
              ...ru,
              rol: ru.rol.map((rol) => {
                if (rol.id === selectedRole) {
                  return {
                    id: rol.id,
                    unidades: unidadesMap,
                  }
                }

                return rol
              }),
            }
          }

          return ru
        }),
      }))
    }
  }

  useEffect(() => {
    onUserUpdate?.({...userUpdated, id})
  }, [userUpdated])

  const unidadesBySelectedRole =
    userUpdated.roleUnidad
      ?.find((ru) => ru.company === companyId)
      ?.rol.find((r) => r.id === selectedRole)
      ?.unidades?.map((u) => u.id) ?? []

  const handleUpdate = async () => {
    try {
      setLoading(true)
      const response = await updateUserByUnitsXPerfil(id, userUpdated.roleUnidad)

      if (response.success) {
        showMessage('success', 'Actualizado correctamente', 'Se actualizo correctamente el usuario')
      } else {
        showMessage('error', 'Hubo un error al actualizar el usuario', 'Error al actualizar')
      }
    } catch (err) {
      showMessage('error', 'Hubo un error al actualizar el usuario', (err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Accordion.Item eventKey={dni} key={dni}>
      <Accordion.Header>
        <Form.Check
          label={`${dni} - ${nombre}`}
          checked={checked}
          onChange={(e) => onCheck?.(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      </Accordion.Header>
      <Accordion.Body>
        <Card>
          <Card.Header className='flex-column'>
            <p className='m-0'>Resumen de asignaciones:</p>
            <div className='d-flex flex-wrap gap-2'>
              {userUpdated?.roleUnidad
                ?.find((ru) => ru.company === companyId)
                ?.rol?.map((r) => (
                  <Badge bg='info' className='text-white' key={r.id}>
                    {roles.find((rol) => rol.id === r.id)?.name} - {r.unidades.length} unidades
                  </Badge>
                ))}
            </div>
          </Card.Header>

          <Card.Body>
            <Select
              placeholder='Selecione un rol'
              // menuPortalTarget={document.body}
              options={rolesOptions}
              ref={selectRef}
              onChange={(e) => {
                setSelectedRole(e?.value ?? '')
              }}
            />
            {selectedRole && (
              <section className='mt-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <p>Unidades para {roles.find((rol) => rol.id === selectedRole)?.name}</p>
                  <p className='text-info'>{unidades.length} unidad(es) en total</p>
                </div>
                <div className='d-flex flex-column gap-2'>
                  {unidades.length > 1 && (
                    <Form.Check
                      inline
                      label='Todos'
                      type='checkbox'
                      id='all'
                      onChange={handleCheck}
                      checked={unidades.every((u) => unidadesBySelectedRole.includes(u.codUnid))}
                    />
                  )}
                  {unidades.map((unidad) => (
                    <Form.Check
                      key={unidad.codUnid}
                      inline
                      label={unidad.DesUnid}
                      type='checkbox'
                      id={unidad.codUnid}
                      onChange={handleCheck}
                      checked={unidadesBySelectedRole.includes(unidad.codUnid)}
                    />
                  ))}
                </div>
              </section>
            )}
          </Card.Body>

          <Card.Footer>
            <Button variant='success' className='w-100' onClick={handleUpdate} disabled={loading}>
              Actualizar
            </Button>
          </Card.Footer>
        </Card>
      </Accordion.Body>
    </Accordion.Item>
  )
}

export {UserItem}
